import { useCurrentSubscriptionPeriod } from '@/app/entities/payment/model/useCurrentSubscriptionPeriod'
import { useEffect, useMemo, useState } from 'react'
import { useCurrentOrg } from '@/app/entities/org/model'
import { payWithCloudPayments, useCloudPaymentsWidget } from '@/app/entities/cloudPayments'
import { closeInsufficientBalanceModal } from '@/app/entities/insufficientBalance/model'
import { openPaymentResultModal } from '@/app/entities/paymentResult'
import { useSubscribePayment } from '@/app/entities/subscribe/model/useSubscribePayment'
import { useSubscriptionsList } from '@/app/entities/subscribe/model/useSubscriptionsList'
import { useMe } from '@/common/entities/me/model/me'
import { trackPackSuccessByPackageId } from '@/common/lib/yandexMetrica/paymentGoals'

const CLOUDPAYMENTS_PUBLIC_ID = __CLOUDPAYMENTS_PUBLIC_ID__

const TOKEN_PACKAGES = [
    { id: 1, tokens: 500, price: 2000, name: 'Хватает на 20 генераций' },
    { id: 2, tokens: 1500, price: 5400, name: 'Хватает на 60 генераций' },
    { id: 3, tokens: 15000, price: 48000, name: 'Хватает на 600 генераций' },
] as const

export type TopUpOptionType = 'subscription' | 'tokens'

export type TopUpOption =
    | {
          type: 'subscription'
          id: string
          tokens: number
          price: number
          old_price?: number | null
          label: string
          period: 'Day' | 'Month'
          num_periods: number
      }
    | { type: 'tokens'; id: number; tokens: number; price: number; label: string; description: string }

type UseTopUpFormOptions = {
    onSuccess?: () => void
}

const getOptionKey = (option: TopUpOption) => `${option.type}:${option.id}`

export const useTopUpForm = ({ onSuccess }: UseTopUpFormOptions = {}) => {
    const { data: currentOrg } = useCurrentOrg()
    const { me } = useMe()
    const { subscriptions, isLoading: isSubscriptionsLoading, error: subscriptionsError } = useSubscriptionsList()
    const {
        subscription: activeSubscription,
        currentPeriod: activeSubscriptionPeriod,
        hasActiveSubscription,
        isLoading: isActiveSubscriptionLoading,
    } = useCurrentSubscriptionPeriod(currentOrg?.id)
    const {
        handleSubscribe,
        isPaying: isSubscriptionPaying,
        isWidgetReady,
        paymentError: subscriptionPaymentError,
    } = useSubscribePayment({ onSuccess, reopenSource: 'insufficient-balance' })
    const [isTokenPaying, setIsTokenPaying] = useState(false)
    const [paymentError, setPaymentError] = useState<string | null>(null)
    const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(null)
    const [selectionError, setSelectionError] = useState<string | null>(null)

    const {
        isWidgetReady: isTokenWidgetReady,
        widgetError,
        getWidgetInstance,
    } = useCloudPaymentsWidget({
        onClose: () => setIsTokenPaying(false),
    })

    const isAdmin = Boolean(me?.is_admin)

    const subscriptionOptions = useMemo<TopUpOption[]>(() => {
        if (hasActiveSubscription && !isAdmin) return []

        return subscriptions.map(plan => ({
            type: 'subscription' as const,
            id: plan.id,
            tokens: plan.tokens,
            price: plan.price,
            old_price: plan.old_price,
            label: plan.name,
            period: plan.period,
            num_periods: plan.num_periods,
        }))
    }, [hasActiveSubscription, isAdmin, subscriptions])

    const tokenOptions = useMemo<TopUpOption[]>(
        () =>
            TOKEN_PACKAGES.map(pkg => ({
                type: 'tokens' as const,
                id: pkg.id,
                tokens: pkg.tokens,
                price: pkg.price,
                label: `${pkg.tokens} токенов`,
                description: pkg.name,
            })),
        []
    )

    const options = useMemo<TopUpOption[]>(
        () => [...subscriptionOptions, ...tokenOptions],
        [subscriptionOptions, tokenOptions]
    )

    const isPaying = isSubscriptionPaying || isTokenPaying
    const isReady = isWidgetReady && isTokenWidgetReady

    const handleOptionSelect = (optionKey: string) => {
        setSelectedOptionKey(optionKey)
        setSelectionError(null)
    }

    const payTokens = (packageId: number) => {
        const selectedPackage = TOKEN_PACKAGES.find(pkg => pkg.id === packageId)
        if (!selectedPackage || !currentOrg?.id || !me?.id) {
            setPaymentError('Не удалось определить данные для оплаты.')
            return
        }

        const widget = getWidgetInstance()
        if (!widget) {
            setPaymentError('Платежный виджет не инициализировался.')
            return
        }

        const invoicePostfix = Math.floor(Date.now() / 1000)
        const invoiceId = `TOKENS_${currentOrg.id}_${selectedPackage.id}_${invoicePostfix}`
        const purchaseLabel = selectedPackage.name
        const amount = selectedPackage.price
        const tokens = selectedPackage.tokens

        setPaymentError(null)
        setIsTokenPaying(true)

        payWithCloudPayments({
            widget,
            options: {
                publicId: CLOUDPAYMENTS_PUBLIC_ID,
                description: `Докупка ${selectedPackage.tokens} токенов (${selectedPackage.name})`,
                amount: selectedPackage.price,
                currency: 'RUB',
                invoiceId,
                accountId: me.id,
                ...(me.email ? { email: me.email } : {}),
                data: {
                    organization_id: currentOrg.id,
                    user_id: me.id,
                    type: 'additional_tokens',
                    package_id: selectedPackage.id,
                    tokens: selectedPackage.tokens,
                },
            },
            onSuccess: () => {
                setIsTokenPaying(false)
                trackPackSuccessByPackageId(selectedPackage.id)
                closeInsufficientBalanceModal()
                openPaymentResultModal({
                    status: 'success',
                    purchaseType: 'tokens',
                    purchaseLabel,
                    amount,
                    tokens,
                    reopenSource: 'insufficient-balance',
                    onSettledSuccess: onSuccess,
                })
            },
            onFail: result => {
                setIsTokenPaying(false)
                closeInsufficientBalanceModal()
                openPaymentResultModal({
                    status: 'fail',
                    purchaseType: 'tokens',
                    purchaseLabel,
                    amount,
                    tokens,
                    failReason: result.reason,
                    reopenSource: 'insufficient-balance',
                })
            },
        })
    }

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault()
        if (isPaying) return

        if (!selectedOptionKey) {
            setSelectionError('Пожалуйста, выберите вариант.')
            return
        }

        const selectedOption = options.find(option => getOptionKey(option) === selectedOptionKey)
        if (!selectedOption) {
            setSelectionError('Пожалуйста, выберите вариант.')
            return
        }

        if (!isReady) {
            setPaymentError('Платежный виджет еще не загружен.')
            return
        }

        if (selectedOption.type === 'subscription') {
            handleSubscribe(selectedOption.id, {
                purchaseLabel: selectedOption.label,
                tokens: selectedOption.tokens,
            })
            return
        }

        payTokens(selectedOption.id)
    }

    useEffect(() => {
        if (options.length === 0) return
        if (selectedOptionKey && options.some(option => getOptionKey(option) === selectedOptionKey)) return
        setSelectedOptionKey(getOptionKey(options[0]))
    }, [options, selectedOptionKey])

    return {
        subscriptionOptions,
        tokenOptions,
        options,
        hasActiveSubscription,
        activeSubscription,
        activeSubscriptionPeriod,
        isLoading: isSubscriptionsLoading || isActiveSubscriptionLoading,
        loadError: subscriptionsError,
        selectedOptionKey,
        isPaying,
        isReady,
        resolvedError: selectionError ?? paymentError ?? subscriptionPaymentError ?? widgetError,
        handleOptionSelect,
        handleSubmit,
        getOptionKey,
    }
}
